import cx from 'classnames';
import { useContext } from 'react';
import { type TileArgs } from 'react-calendar';
import { Calendar } from '@components/calendar/Calendar';
import { Divider } from '@components/divider/Divider';
import { GuestCountPicker } from '@components/guestCountPicker/GuestCountPicker';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { useAvailableDates } from 'webReservations/hooks/useAvailableDates';
import typography from '~styles/typography.scss';
import { useAvailabilityContext } from '../context/AvailabilityContext';
import { GuestPricingBrowseContext } from 'webReservations/pricingAnalytics/GuestPricingBrowseContext';
import {
  sendGa4AvailabilityFilterDateUpdateEvent,
  sendGa4AvailabilityFilterGuestUpdateEvent,
} from '../availability/gaHelpers';
import styles from './DateAndGuestCountFilterPopover.scss';
import { MaxGuestCountCopy } from './MaxGuestCountCopy';

interface DateAndGuestCountFilterPopoverProps {
  calendarClassName?: string;
  maxGuestCountMessageClassName?: string;
}

export const DateAndGuestCountFilterPopover = ({
  calendarClassName,
  maxGuestCountMessageClassName,
}: DateAndGuestCountFilterPopoverProps) => {
  const { restaurantDetails } = useRestaurantContext();
  const { availableDates } = useAvailableDates();
  const {
    selectedDate,
    setSelectedDate,
    selectedGuestCount,
    setSelectedGuestCount,
  } = useAvailabilityContext();
  const guestPricingBrowse = useContext(GuestPricingBrowseContext);
  const resetServiceWindowIndex =
    guestPricingBrowse?.resetServiceWindowIndex ?? (() => undefined);

  let noTimes;

  if (selectedGuestCount > restaurantDetails.maxReservationGuests) {
    noTimes = (
      <MaxGuestCountCopy messageClassName={maxGuestCountMessageClassName} />
    );
  }

  const tileDisabledHandler = ({ date }: TileArgs) =>
    availableDates ? !availableDates.includes(toISODateFormat(date)) : false;

  return (
    <div
      className={styles.popover}
      data-testid="date-and-guest-count-filter-popover"
    >
      <GuestCountPicker
        className={styles.guestCountPicker}
        guestCount={selectedGuestCount}
        max={restaurantDetails.maxReservationGuests + 1}
        onChange={(newGuestCount) => {
          resetServiceWindowIndex();
          setSelectedGuestCount(newGuestCount);
          sendGa4AvailabilityFilterGuestUpdateEvent({
            restaurantName: restaurantDetails.name,
            guestCount: newGuestCount,
          });
        }}
      />
      <Divider className={styles.divider} />
      {noTimes || (
        <>
          <h2 className={cx(typography.h7, styles.selectDate)}>Select Date</h2>
          <Calendar
            className={calendarClassName}
            value={selectedDate}
            onDateChange={(newDate: Date) => {
              resetServiceWindowIndex();
              setSelectedDate(newDate);
              sendGa4AvailabilityFilterDateUpdateEvent({
                restaurantName: restaurantDetails.name,
                date: toISODateFormat(newDate),
              });
            }}
            tileDisabled={tileDisabledHandler}
          />
        </>
      )}
    </div>
  );
};
